import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useDashboardTheme, cardSx, panelHeaderSx, tableHeadRowSx, tableRowSx, scrollbarSx, fmt, fmtBtc, fmtTimeShort } from '../utils';

function BossCyclePanel({ liveState, bossCycles, loading }) {
  const t = useDashboardTheme();
  const boss = liveState?.boss;
  const cycleData = bossCycles?.data || [];

  return (
    <Card sx={cardSx}>
      <MDBox sx={panelHeaderSx(t)}>
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
          Boss Cycle
        </MDTypography>
      </MDBox>

      {/* Current boss */}
      {boss?.userId ? (
        <MDBox sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
          <MDBox display='flex' alignItems='center' gap={1} mb={0.5}>
            <Chip label='Current Boss' color='warning' size='small' sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
            <MDTypography variant='body2' fontWeight='bold' sx={{ color: '#fbbf24' }}>
              {boss.name}
            </MDTypography>
          </MDBox>
          <MDTypography variant='caption' sx={{ color: t.secondaryText }}>
            Winning Bid: ${fmt(boss.winningBid)} &nbsp;|&nbsp; Winnings: ${fmt(boss.winnings)}
            &nbsp;|&nbsp; Highest: ${fmt(boss.highestBid)} / {fmtBtc(boss.highestBidBtc)} BTC
          </MDTypography>
          {boss.topBidders?.length > 0 && (
            <MDBox mt={1}>
              <MDTypography variant='caption' fontWeight='medium' sx={{ color: t.primaryText }}>
                Top Bidders:
              </MDTypography>
              {boss.topBidders.map((tb, i) => (
                <MDTypography key={tb.userId} variant='caption' display='block' sx={{ pl: 1, color: t.secondaryText }}>
                  {i + 1}. {tb.name} — ${fmt(tb.bid)}
                </MDTypography>
              ))}
            </MDBox>
          )}
        </MDBox>
      ) : (
        <MDBox sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
          <MDTypography variant='body2' sx={{ color: t.secondaryText, opacity: 0.6 }}>No active boss</MDTypography>
        </MDBox>
      )}

      <Divider sx={{ borderColor: t.divider }} />

      {/* Boss cycle history */}
      <MDBox sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
        <MDTypography variant='subtitle2' fontWeight='bold' mb={1} sx={{ fontSize: '0.85rem', color: t.primaryText }}>
          Boss Cycle History ({bossCycles?.total ?? 0})
        </MDTypography>
        {loading && cycleData.length === 0 ? (
          <Skeleton variant='rectangular' height={120} sx={{ borderRadius: '8px' }} />
        ) : cycleData.length === 0 ? (
          <MDTypography variant='body2' sx={{ opacity: 0.45, color: t.secondaryText }}>No completed boss cycles</MDTypography>
        ) : (
          <TableContainer sx={{ ...scrollbarSx(t), maxHeight: 240 }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow sx={tableHeadRowSx(t)}>
                  <TableCell>Time</TableCell>
                  <TableCell>Boss</TableCell>
                  <TableCell align='right'>Bid ($)</TableCell>
                  <TableCell align='right'>Winnings ($)</TableCell>
                  <TableCell align='right'>Bidders</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cycleData.map((cycle, index) => (
                  <TableRow key={cycle.id} sx={tableRowSx(t, { index })}>
                    <TableCell>
                      <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText }}>{fmtTimeShort(cycle.cycleEndedAt)}</MDTypography>
                    </TableCell>
                    <TableCell>
                      <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.78rem', color: t.primaryText }}>{cycle.bossName || '—'}</MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(cycle.winningBidUsd)}</MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(cycle.bossWinningsUsd)}</MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{cycle.topBidders?.length ?? 0}</MDTypography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MDBox>
    </Card>
  );
}

export default BossCyclePanel;
